import {
    Dispatch,
    MouseEvent,
    RefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useState,
    MutableRefObject,
    useRef,
    useImperativeHandle,
    forwardRef,
} from "react";
import styles from "./POIListView.module.css";
import ContentTitle from "./ContentTitle";
import { CategoryOptions, POIInfo } from "../../common/Common";
import SearchPOI, { POISearchOptions } from "./SearchPOI";
import SeletedCategory from "./SeletedCategory";
import POIListItem from "./POIListItem";
import POIScrollView from "../POIScrollView";
import { useSearchParams } from "react-router-dom";

import BottomButtonBox, { ButtomButtonBoxProps } from "../BottomButtonBox";
import { ContentList, MapToolType } from "../../../screens/MapScreen";
import { MiniButtonType } from "../MiniButton";

import { ResponseType } from "../../../data_provider/gateways/Gateway";
import DataProvider from "../../../data_provider/DataProvider";
import { ListSearchReqBody } from "../../../data_provider/gateways/MapGateway";

interface POIListViewProps {
    POIList: RefObject<HTMLDivElement>;
    categories: CategoryOptions[];
    // 25.09.02 | Kim Young Jun | selectedCategoryArray 기반으로 변경
    selectedCategoryArray: number[];
    selectedMapIdx: number;
    setSelectedCategory: Dispatch<SetStateAction<number>>;
    selectedPoiIdx: number | string;
    setSelectedPoiIdx: Dispatch<SetStateAction<number | string>>;
    analysisXIDs: number[] | undefined; // 시간별 이동거리, 접근성 분석, 범위분석 공통
    setAnalysisXIDs: Dispatch<SetStateAction<number[] | undefined>>;
    overlapMapId?: string;
    changeContent: (type: ContentList, reset?: boolean) => void;
    activeTool: MapToolType;
    setClearByButton: Dispatch<SetStateAction<number>>;
    analysisStage: number;
    setResetAcc: Dispatch<SetStateAction<boolean>>;
    prevSelectedCategoryRef?: MutableRefObject<number | null>;

    searchOptions: POISearchOptions | undefined; // 검색 옵션
    setSearchOptions: Dispatch<SetStateAction<POISearchOptions | undefined>>;
    listSearchOptions: ListSearchReqBody; // 목록 검색 옵션
    setListSearchOptions: Dispatch<SetStateAction<ListSearchReqBody>>;

    // 25.07.17 | Kim Young Jun | 분석도구 활성화 여부
    isNotUseMarkerAnalysis?: boolean; // 마커를 사용하지 않는 분석도구 활성화 여부
}

export interface POIListViewRef {
    reload: () => void;
}

export interface SortingStatus {
    sortBy: string;
    direction: string;
}

export interface ScrollViewListResult {
    list: Array<any>;
    count: number;
    pages: number;
}

/**
 * @author bakim, smyun
 * @date 2024-09-12 13:31:56, 2025-05-26
 * @remark 사이드 시트 - 장소 목록 화면
 * @param props POIListViewProps
 * @returns <POIListView>
 */
const POIListView = forwardRef<POIListViewRef, POIListViewProps>(({ POIList, categories, selectedPoiIdx, activeTool, ...props }, ref) => {
    const isEqualSorted = (a: number[] | null | undefined, b: number[] | null | undefined): boolean => {
        if (a == null && b == null) return true;
        if (a == null || b == null || a.length !== b.length) return false;
        return [...a].sort().join() === [...b].sort().join();
    };

    const isSameCategory = (baseCategrory: string | undefined, newCategory: string | undefined): boolean => {
        if (baseCategrory === undefined && newCategory === "전체") return true;
        if (baseCategrory === newCategory) return true;

        return false;
    };

    const BUNDLE_SIZE = 40; // 로드할 데이터 개수

    /** States, Ref ------------------------------------------------------------------------------------ */
    const [count, setCount] = useState<number>(0);
    const [params] = useSearchParams();
    const [mapId, setMapId] = useState(params.get("id") as string);

    const [renderData, setRenderData] = useState<object[]>();

    const [isSorting, setIsSorting] = useState<SortingStatus>({ sortBy: "XID", direction: "asc" });

    const [scrollPos, setScrollPos] = useState<number | null>(null); // 스크롤 위치(새로 데이터를 채운 뒤 포커스할 인덱스)

    const lastPage = useRef<number>(1);
    const page = useRef<number>(1);

    const lastRequestId = useRef<number>(0);

    useImperativeHandle(ref, () => ({
        reload,
    }));

    /** useEffect ------------------------------------------------------------------------------------------------- */
    // 25.09.03 | Kim Young Jun | 통합된 필터 계산 및 API 호출 최적화
    useEffect(() => {
        // mapId가 없거나 categories가 비어있으면 처리하지 않음
        if (!mapId || categories.length === 0) {
            return;
        }

        // 현재 선택된 카테고리 계산
        const currentSelectedIdx = props.selectedCategoryArray[props.selectedMapIdx] ?? 0;
        
        // 카테고리 설정 로직
        let newCategory: string | undefined = undefined;
        if (currentSelectedIdx > 0 && currentSelectedIdx < categories.length) {
            const categoryItem = categories[currentSelectedIdx];
            if (categoryItem && categoryItem.label) {
                newCategory = categoryItem.label;
            }
        }
        
        const newSearch = props.searchOptions?.search ?? "";
        // 오버랩 모드에서는 analysisXIDs 사용하지 않음
        const newXIDs = props.selectedMapIdx > 0 ? undefined : (props.analysisXIDs ?? props.searchOptions?.XID);

        // 필터 변경 여부 체크
        const oldFilter = props.listSearchOptions.filter as POISearchOptions | undefined;
        const categoryChanged = !isSameCategory(oldFilter?.category, newCategory);
        const searchChanged = oldFilter?.search !== newSearch;
        const xidsChanged = !isEqualSorted(oldFilter?.XID, newXIDs);
        const sortChanged = props.listSearchOptions.sortBy !== isSorting.sortBy || 
                           props.listSearchOptions.direction !== isSorting.direction;

        // 변경된 경우에만 업데이트
        if (categoryChanged || searchChanged || xidsChanged || sortChanged) {
            const filter: POISearchOptions = {
                search: newSearch,
                XID: newXIDs,
            };
            
            if (newCategory) {
                filter.category = newCategory;
            }

            const newOptions: ListSearchReqBody = {
                page: 1,
                count: BUNDLE_SIZE,
                filter,
                sortBy: isSorting.sortBy,
                direction: isSorting.direction,
            };

            props.setListSearchOptions(newOptions);
        }
    }, [
        mapId,
        categories,
        props.selectedCategoryArray,
        props.selectedMapIdx,
        props.searchOptions?.search,
        props.analysisXIDs,
        isSorting.sortBy,
        isSorting.direction
    ]);

    // API 호출 전용 useEffect
    useEffect(() => {
        if (mapId && props.listSearchOptions) {
            // 직접 API 호출 로직 실행
            const currentRequestId = ++lastRequestId.current;

            const fetch = async () => {
                if (!mapId) return;
                
                const response = await DataProvider.GetPlaceList({ mapid: mapId }, props.listSearchOptions);

                if (!response || !response.result) {
                    setRenderData([]);
                    setCount(0);
                    return;
                }

                const result = response.result;

                if (currentRequestId === lastRequestId.current) {
                    setRenderData(result.list ?? []);
                    setCount(result.count ?? 0);
                    lastPage.current = result.pages ?? 1;
                    page.current = 1;
                    setScrollPos(0);
                    setTimeout(() => setScrollPos(null), 0);
                }
            };

            fetch();
        }
    }, [props.listSearchOptions, mapId]);

    // 오버랩 지도 ID 변경 처리
    useEffect(() => {
        const newMapId = props.overlapMapId ?? (params.get("id") as string);
        
        // mapId가 실제로 변경되었을 때만 업데이트
        if (newMapId && newMapId !== mapId) {
            setMapId(newMapId);
            
            // 25.09.03 | Kim Young Jun | 오버랩 지도 전환 시 분석결과 XID 초기화
            // 분석도구 결과가 설정되어 있는 경우, 지도 전환 시 초기화하여 모든 장소가 보이도록 함
            if (props.analysisXIDs && props.analysisXIDs.length > 0) {
                props.setAnalysisXIDs(undefined);
            }
        }
    }, [props.overlapMapId, mapId, params, props.analysisXIDs, props.setAnalysisXIDs]);

    // 정렬 변경 핸들러 - 상태만 업데이트 (API 호출은 위 useEffect에서 처리)
    const handleSortingChange = useCallback((value: React.SetStateAction<SortingStatus>) => {
        const newSorting = typeof value === 'function' ? value(isSorting) : value;
        setIsSorting(newSorting);
    }, [isSorting]);


    // API ------------------------------------------------------------------------------------

    const isLoading = useRef(false);

    // 스크롤 Bottom
    const handleIsBottom = async () => {

        if (isLoading.current || page.current === lastPage.current) return;

        isLoading.current = true;

        const _requestOptions: ListSearchReqBody = props.listSearchOptions;
        _requestOptions.page = page.current + 1;
        
        // 직접 API 호출
        if (!mapId) return;
        const response = await DataProvider.GetPlaceList({ mapid: mapId }, _requestOptions);

        if (response.flag && response.result?.list.length > 0) {
            page.current += 1;
            // // 기존 데이터
            // let data: Object[] = renderData ? [...renderData] : [];

            // // 데이터 갱신
            // const result: ScrollViewListResult = response.flag && response.result ? response.result : { list: [], pages: 0 };
            // data.push(...result.list);

            // const startIndex = page.current < 3 ? 0 : BUNDLE_SIZE;
            // data = data.slice(startIndex);

            // setRenderData(data);

            // const _pos = Math.min(data.length, BUNDLE_SIZE);
            // setScrollPos(_pos);
            setRenderData((prevData) => [...(prevData || []), ...response.result.list]);
        }

        isLoading.current = false;
    };

    // 스크롤 Top
    const handleIsTop = async () => {
        if (isLoading.current || page.current === 1) return;

        isLoading.current = true;

        const _requestOptions: ListSearchReqBody = props.listSearchOptions;
        _requestOptions.page = page.current - 1
        
        // 직접 API 호출
        if (!mapId) return;
        const response = await DataProvider.GetPlaceList({ mapid: mapId }, _requestOptions);

        if (response.flag && response.result?.list.length > 0) {
            page.current -= 1

            // let data: Object[] = renderData ? [...renderData] : [];
            // const result: ScrollViewListResult = response.flag && response.result ? response.result : { list: [], pages: 0 };
            // data = [...result.list, ...data];

            // data = data.slice(0, BUNDLE_SIZE * 2);
            // setRenderData(data);

            // setScrollPos(result.list.length);
            setRenderData((prevData) => [...response.result.list, ...(prevData || [])]);
        }

        isLoading.current = false;
    };

    // 리로드
    const reload = useCallback(() => {
        // 직접 API 호출 로직 실행
        const currentRequestId = ++lastRequestId.current;

        const fetch = async () => {
            if (!mapId) return;
            
            const response = await DataProvider.GetPlaceList({ mapid: mapId }, props.listSearchOptions);

            if (!response || !response.result) {
                setRenderData([]);
                setCount(0);
                return;
            }

            const result = response.result;

            if (currentRequestId === lastRequestId.current) {
                setRenderData(result.list ?? []);
                setCount(result.count ?? 0);
                lastPage.current = result.pages ?? 1;
                page.current = 1;
                setScrollPos(0);
                setTimeout(() => setScrollPos(null), 0);
            }
        };

        fetch();
    }, [mapId, props.listSearchOptions]);

    /** Render ------------------------------------------------------------------------------------------- */
    const renderItem = (item: POIInfo, index: number) => {
        const selectPOI = (e: MouseEvent<HTMLElement>) => {
            // 25.07.17 | Kim Young Jun | 분석 활성화 상태에서 장소 선택 되지 않도록
            if (props.isNotUseMarkerAnalysis) return; // 마커를 사용하지 않는 분석 활성화 상태에서 장소 선택 되지 않도록

            POIList.current &&
                Array.from(POIList.current.children).forEach((child: Element) => {
                    child.classList.remove("selected");
                });
            
            e.currentTarget.classList.add("selected");

            props.setSelectedPoiIdx(item.XID);
        };
        return <POIListItem key={`poi_${index}`} item={item} onClickItem={selectPOI} selected={Number(selectedPoiIdx) === item.XID} />;
    };

    const reset = () => {
        switch (activeTool) {
            case MapToolType.Direction:
                props.setClearByButton(0);
                props.setAnalysisXIDs(undefined);
                props.changeContent(ContentList.Direction, true);
                break;
            case MapToolType.Accessibility:
                props.setResetAcc(true);
                props.changeContent(ContentList.Accessibility);
                break;
        }
    };
    const prev = () => {
        switch (activeTool) {
            case MapToolType.Direction:
                props.setClearByButton(props.analysisStage);
                props.setAnalysisXIDs(undefined);
                props.changeContent(ContentList.Direction);
                break;
            // case MapToolType.Accessibility:
            //     // action
            //     break;
        }
    };

    // 분석도구에 따라 buttonOptions 표현. (key 값이 없으면 BottomButtonBox 표현하지 않음) | 2025.04.09 | smyun
    const buttonOptions: Partial<Record<MapToolType, ButtomButtonBoxProps>> = {
        [MapToolType.Direction]: {
            left: { label: "초기화", type: MiniButtonType.RED, onClick: reset },
            right: { label: "이전", type: MiniButtonType.GRAY, onClick: prev },
        },
        [MapToolType.Accessibility]: {
            // left: { label: "초기화", type: MiniButtonType.RED, onClick: reset },
            // right: { label: "이전", type: MiniButtonType.GRAY, onClick: prev },
            right: { label: "초기화", type: MiniButtonType.RED, onClick: reset },
        },
    };

    return (
        <div className={styles.container}>
            <SearchPOI searchOptions={props.searchOptions!} setSearchOptions={props.setSearchOptions} />
            <SeletedCategory
                categories={categories}
                selectedCategory={props.selectedCategoryArray[props.selectedMapIdx] ?? 0}
                setSelectedCategory={props.setSelectedCategory}
                setIsSorting={handleSortingChange}
                prevSelectedCategoryRef={props.prevSelectedCategoryRef}
            />
            <ContentTitle
                title={"검색결과"}
                keyword={
                    props.searchOptions && props.searchOptions.search 
                        ? props.searchOptions.search 
                        : (() => {
                            const currentSelectedIdx = props.selectedCategoryArray[props.selectedMapIdx] ?? 0;
                            // 25.09.02 | Kim Young Jun | 안전한 카테고리 라벨 접근
                            if (currentSelectedIdx > 0 && currentSelectedIdx < categories.length) {
                                const categoryItem = categories[currentSelectedIdx];
                                if (categoryItem && categoryItem.label) {
                                    return categoryItem.label;
                                }
                            }
                            return "전체";
                        })()
                }
                count={count}
            />
            <POIScrollView renderItem={renderItem} renderData={renderData} isTop={handleIsTop} isBottom={handleIsBottom} scrollPos={scrollPos} />
            {buttonOptions[activeTool] && <BottomButtonBox {...(buttonOptions[activeTool!] as ButtomButtonBoxProps)} />}
        </div>
    );
});

export default POIListView;
